<script setup>
import { useForm } from 'vee-validate'
import { storeToRefs } from 'pinia'
import Coupon from 'assets/icons/coupon.svg'
import Money from 'assets/icons/money.svg'
import Illustration from 'assets/illustrations/illu-03.svg?url'
import Illustration2 from 'assets/illustrations/illu-02.svg?url'
import { useMessageStore } from '~/stores/messages'
import { useParkingStore } from '~/stores/parking'
import { PARKING_CONSTANTS } from '@/utils/parkingConstants'

// Get Auth states
const bearerToken = useState('state_bearerToken')

// Get a locale path
const localePath = useLocalePath()

// Get center route
const route = useRoute()
const centerRoute = computed(() => route.params?.centerUid)

// Fetch messages
const messagesStore = useMessageStore()
const { messages: staticKeys } = storeToRefs(messagesStore)

// Error hint
const errorHintVisible = ref(false)

const toggleErrorHint = (value) => {
    errorHintVisible.value = value
}

// Get center details
const { data: centerDetails, suspense: centerDetailSuspense } = useFetchCenterDetails(centerRoute.value)

onServerPrefetch(async () => {
    await centerDetailSuspense()
})

// Define scroll trigger
const voucherElement = ref(null)

// Fetch parking store
const parkingStore = useParkingStore()

const {
    ticketInfos,
    ticketFormValidationStatus,
    ticketStatus,
    savedTicketVoucher,
} = storeToRefs(parkingStore)

const {
    handleGetTicketInfoSuccess,
    handleGetTicketInfoError,
    updateTicketInfos,
    updateTicketPaymentProgress,
    updateTicketFormValidationStatus,
    updateTicketStatus
} = parkingStore

// Prepare form (prefill this values with stored data from parking store)
const { meta, values, handleSubmit, isSubmitting } = useForm({
    voucher: savedTicketVoucher.value
})

// Capitalize the first letter and combine it with the rest of the string
const feesHeadlineConfig = computed(() => {
    return {
        headline: {
            title: staticKeys.value?.parking_step2_cost_headline.charAt(0)?.toUpperCase() + staticKeys.value?.parking_step2_cost_headline?.toLowerCase()?.slice(1)
        },
        icon: {
            name: Money
        }
    }
})

// Config for voucher headline
const voucherHeadlineConfig = computed(() => {
    return {
        headline: {
            title: staticKeys.value?.parking_step2_coupon_subheadline
        },
        icon: {
            name: Coupon
        }
    }
})

// Config for voucher input field
const voucherInputFieldConfig = computed(() => {
    return {
        fieldType: 'text',
        id: 'voucher',
        name: 'voucher',
        pending: isSubmitting.value,
        placeholder: staticKeys.value?.parking_step2_coupon_code,
        lightMode: true,
        large: true,
        val: savedTicketVoucher.value,
        visualFeedback: ticketFormValidationStatus.value?.voucher === 'success',
        successIcon: ticketFormValidationStatus.value?.voucher === 'success',
        successLabel: staticKeys.value?.parking_step2_coupon_success?.replace('%s', values.voucher),
        serverError: {
            error: ticketFormValidationStatus.value?.voucher === 'error',
            message: staticKeys.value?.parking_step2_coupon_error
        },
    }
})

// Config for voucher form submit button
const voucherFormSubmitButtonConfig = computed(() => {
    return {
        visible: true,
        label: staticKeys.value?.parking_step2_coupon_cta,
        buttonConfig: {
            type: 'submit',
            buttonType: 'secondary',
            isPending: isSubmitting.value,
            large: true,
            active: isActive.value
        }
    }
})

// Active state button
const isActive = computed(() => {
    if (!values.voucher) {
        return false
    }

    return meta.value?.valid
})

const getFormClass = computed(() => {
    return {
        'is-submitting': isSubmitting.value
    }
})

// Construct payload for fetch ticket infos
const payload = computed(() => {
    return {
        bearerToken,
        ...values,
        searchTermType: ticketInfos.value?.searchTermType,
        searchTerm: ticketInfos.value?.searchTerm,
        centerUid: centerRoute.value,
    }
})

// Fetch ticket infos (we have to use reactive payload to trigger refetch)
const { refetch } = useFetchTicketInfo(payload)
/**
 *  Function to handle form submit
 * @type {(e?: Event) => Promise<Promise<boolean|undefined> | undefined>}
 */
const onVoucherSubmit = handleSubmit(async () => {
    const voucher = payload.value?.voucher

    // Prevent empty voucher submit
    if (!voucher) {
        return false
    }

    // Track user click
    trackUserClick({
        eventName: TRACKING_CONSTANTS.CLICK_EVENTS.PARKING_TICKET_PAYMENT_STEP2_CLICK,
        voucher
    })

    // Trigger refetch to get ticket infos
    const { data, isSuccess, isError } = await refetch()

    // Handle success
    if (isSuccess) {
        handleGetTicketInfoSuccess({
            data,
            voucher,
            voucherElement: voucherElement.value
        })
    }

    if (isError) {
        handleGetTicketInfoError({
            message: staticKeys.value?.parking_step2_pay_error
        })
    }
})

onMounted(() => {
    // Scroll to top of page
    if (import.meta.client) {
        window.scrollTo({
            top: 0,
            behavior: 'smooth'
        })
    }
})

const transaction = useState('state_parkingPaymentTransactionId')

const parkingIsFree = computed(() => ticketInfos.value?.totalPrice <= 0)

/**
 *  Success callback
 * @param payload
 */
const payTicketSuccessCallback = (payload) => {
    const { data } = payload

    if (data.transactionId) {
        transaction.value = data?.transactionId

        // Go to a payment ticket form
        updateTicketPaymentProgress(2)
    } else {
        updateTicketStatus({
            success: false,
            message: staticKeys.value?.parking_step2_pay_error
        })
    }
}

/**
 *  Error callback
 *  @param payload
 */
const payTicketErrorCallback = (payload) => {
    const { error } = payload

    if (!error.response.ok) {
        if (error.response.status === 409) {
            // Update ticket infos in parking store
            updateTicketInfos(error.response?._data?.data?.embeddedData)
            // Show modal with infos (price has changed)
            toggleErrorHint(true)
        } else if (error.response.status === 424) {
            toggleErrorHint(true)
            updateTicketInfos({ duplicateEmailError: true })
        } else {
            updateTicketStatus({
                success: false,
                message: staticKeys.value?.parking_step2_pay_error
            })
        }
    }
}

// Fetch mutation
const { mutate, isPending } = usePayTicketMutation({
    bearerToken: bearerToken.value,
    centerUid: centerRoute.value,
    searchTerm: ticketInfos.value?.searchTerm,
    searchTermType: ticketInfos.value?.searchTermType,
    onSuccessCallback: payTicketSuccessCallback,
    onErrorCallback: payTicketErrorCallback
})

/**
 * Handles ticket form submission:
 * - Resets duplicate email error.
 * - Updates payment pending status.
 * - Submits ticket data via mutation.
 * - Scrolls page to the top (client-side only).
 *
 * @returns {void}
 */
const handleProceedTicketForm = async () => {
    updateTicketInfos({ duplicateEmailError: false })

    if (parkingIsFree.value) {
        await navigateTo(localePath({
            name: 'payment-success',
            query: { scope: PARKING_CONSTANTS.MOBILE_TICKET_PAYMENT }
        }))
    } else {
        mutate({
            body: {
                duration: ticketInfos.value?.duration,
                totalPrice: ticketInfos.value?.totalPrice,
            },
            dynamicUrlPart: savedTicketVoucher.value ? `?voucher=${savedTicketVoucher.value}` : null
        })

        if (import.meta.client) {
            window.scrollTo({
                top: 0,
                behavior: 'smooth'
            })
        }
    }
}

// Config for proceeded button
const proceedButtonConfig = computed(() => {
    return {
        buttonType: 'primary',
        type: 'div',
        large: true,
        active: true,
        fn: async () => await handleProceedTicketForm()
    }
})

const getProceedPaymentButtonTxt = computed(() => parkingIsFree.value ? staticKeys.value?.parking_step2_pay_cta_no_money : staticKeys.value?.parking_step2_pay_cta)

// Button logout config
const logoutButtonConfig = computed(() => {
    return {
        buttonType: 'primary',
        type: 'div',
        active: true,
        large: true,
        fn: async () => await signOut(route)
    }
})

/**
 * Handle server error by updating ticket validation status.
 *
 * @param {Object} target - the target object to update
 */
const handleServerError = (target) => {
    // Update ticket validation status
    updateTicketFormValidationStatus({
        [target.name]: ''
    })
}

// Set config for payment info
const parkingPaymentInfoConfig = computed(() => {
    return {
        startDate: ticketInfos.value?.startDate,
        duration: ticketInfos.value?.durationText,
        totalPrice: getFormattedPrice(ticketInfos.value?.totalPrice, ticketInfos.value?.currency),
        center: centerDetails.value?.centerName
    }
})

/**
 * Resets the status object with the given value.
 *
 * @param {any} value - The new value to set the status object to.
 * @return {void} This function does not return anything.
 */
const resetStatusObj = (value) => {
    updateTicketStatus(value)
}

watch(errorHintVisible, () => {
    if (errorHintVisible.value && !ticketInfos.value?.duplicateEmailError) {
        setTimeout(() => {
            // Wait for some seconds and then proceed
            handleProceedTicketForm()
            toggleErrorHint(false)
        }, 4000)
    }
})

// Track Page Load
trackPageLoad({
    title: TRACKING_CONSTANTS.TITLES.PARKING_TICKET_PAYMENT_STEP2,
})
</script>

<template>
    <div class="parking-payment-proceed-container">
        <FormMessage :form-message-config="ticketStatus" @update:set-status="resetStatusObj" />

        <SideBarHeadline :sidebar-headline-config="{ title: staticKeys?.parking_step2_ticket_headline }" />

        <DefaultContentBox :content-box-config="{ type: 'small' }">
            <ParkingPaymentItem :parking-payment-info="parkingPaymentInfoConfig" />
        </DefaultContentBox>

        <SideBarHeadline :sidebar-headline-config="{ title: staticKeys?.parking_step2_coupon_headline }" />

        <DefaultContentBox :content-box-config="{ type: 'small' }">
            <header class="parking-payment-voucher-header">
                <DefaultContentBoxHeadline :content-box-headline-config="voucherHeadlineConfig" />
            </header>

            <form
                ref="voucherElement"
                class="parking-payment-voucher-form"
                :class="getFormClass"
                autocomplete="off"
                @submit.prevent="onVoucherSubmit"
            >
                <FormText :input-config="voucherInputFieldConfig" @update:update-server-error="handleServerError" />
                <FormButton :form-button-config="voucherFormSubmitButtonConfig" />
            </form>
        </DefaultContentBox>

        <DefaultContentBox class="parking-payment-fees-container" :content-box-config="{ type: 'small' }">
            <header class="parking-payment-fees-header">
                <DefaultContentBoxHeadline :content-box-headline-config="feesHeadlineConfig" />
            </header>

            <table class="parking-payment-fees-table">
                <tbody>
                    <tr>
                        <td>{{ ticketInfos?.ticketRate?.name }}</td>
                        <td>{{ getFormattedPrice(ticketInfos?.ticketRate?.price, ticketInfos?.currency) }}</td>
                    </tr>
                    <tr v-for="fee in ticketInfos?.feeRates" :key="fee.name">
                        <td>{{ fee.name }}</td>
                        <td>{{ getFormattedPrice(fee?.price, ticketInfos?.currency) }}</td>
                    </tr>

                    <template v-for="voucher in ticketInfos?.voucherRates" :key="voucher.name">
                        <tr v-if="voucher.status === 'OK'">
                            <td>{{ voucher.name }}</td>
                            <td>{{ getFormattedPrice(voucher?.amount, ticketInfos?.currency) }}</td>
                        </tr>
                    </template>
                </tbody>
                <tfoot>
                    <tr>
                        <td>
                            {{ staticKeys.parking_step2_cost_total }}
                            <br>
                            <span>{{ staticKeys.parking_step2_cost_taxes }}</span>
                        </td>
                        <td>{{ getFormattedPrice(ticketInfos?.totalPrice, ticketInfos?.currency) }}</td>
                    </tr>
                </tfoot>
            </table>
        </DefaultContentBox>

        <div class="parking-payment-proceed-cta">
            <span v-if="parkingIsFree" class="parking-payment-proceed-helper-text">
                {{ staticKeys?.parking_step2_pay_cta_no_money_helper_text }}
            </span>

            <BaseButton :base-button-config="proceedButtonConfig">
                {{ getProceedPaymentButtonTxt }}
            </BaseButton>
        </div>

        <DevOnly>
            <pre>{{ ticketInfos }}</pre>
        </DevOnly>
    </div>

    <LoadingSpinner v-if="isPending" class="fullscreen-loader" />

    <Transition name="fade">
        <ModalWindow :visible="errorHintVisible" @update:toggle-dialog="toggleErrorHint">
            <div class="parking-payment-hint-content">
                <template v-if="ticketInfos.duplicateEmailError">
                    <h2>Oups...</h2>

                    <img
                        :src="Illustration2"
                        :alt="staticKeys?.parking_step2_cost_increase_headline"
                        class="parking-payment-hint-content-illustration"
                        width="150"
                        height="97"
                    >
                    <p>
                        Leider existiert deine hier verwendete E-mail Adresse  im Parking System bereits unter einem anderen User.
                    </p>
                    <br>
                    <p>
                        Um hier weiter zu machen logge dich bitte aus und registriere Dich erneut mit einer anderen E-Mail Adresse.
                    </p>
                    <br>
                    <p>
                        <BaseButton :base-button-config="logoutButtonConfig">
                            Ausloggen
                        </BaseButton>
                    </p>
                </template>
                <template v-else>
                    <h2>{{ staticKeys?.parking_step2_cost_increase_headline }}</h2>

                    <img
                        :src="Illustration"
                        :alt="staticKeys?.parking_step2_cost_increase_headline"
                        class="parking-payment-hint-content-illustration"
                        width="150"
                        height="97"
                    >
                    <p>
                        {{ staticKeys?.parking_step2_cost_increase_copy1 }}
                        <strong>{{ getFormattedPrice(ticketInfos?.totalPrice, ticketInfos?.currency) }}</strong>

                        <span v-html="staticKeys?.parking_step2_cost_increase_copy2" />
                    </p>
                </template>
            </div>
        </ModalWindow>
    </Transition>
</template>

<style lang="scss">
.parking-payment-proceed-container {
    margin-block: px2rem(24) px2rem(60);

    @include bp(medium1) {
        margin-block: px2rem(40) px2rem(100);
    }
}

.parking-payment-voucher-header {
    width: 100%;
}

.parking-payment-voucher-form {
    width: 100%;

    &.is-submitting {
        @include form-is-submitting;
    }
}

.parking-payment-fees-container {
    margin-block-start: 30px;
}

.parking-payment-fees-header {
    width: 100%;
}

// stylelint-disable no-descending-specificity
.parking-payment-fees-table {
    width: 100%;

    @include body2-medium;

    tbody {
        tr {
            td {
                padding-block: 4px;
                vertical-align: middle;

                &:last-child {
                    text-align: right;
                }
            }

            &:last-child {
                td {
                    padding-block-end: px2rem(30);
                }
            }
        }
    }

    tfoot {
        border-top: 2px solid $parking-payment-fees-table-border-color;
        color: $parking-payment-fees-table;

        @include body2-extrabold;

        tr {
            td {
                padding-block-start: px2rem(30);
                vertical-align: top;

                span {
                    color: $parking-payment-fees-note;

                    @include body3-medium;
                }

                &:last-child {
                    text-align: right;
                }
            }
        }
    }
}

.parking-payment-proceed-cta {
    margin-block-start: px2rem(60);
    text-align: center;
}

.parking-payment-proceed-helper-text {
    @include helper-text;

    display: block;
    margin-block-end: px2rem(15);
}

.parking-payment-hint-content {
    background: $parking-payment-hint-content-bg;
    text-align: center;
    padding: 24px;
    border-radius: 24px;

    @include bp(medium1) {
        padding: 30px 30px 40px;
        margin: 0;
        border-radius: 0;
    }

    h2 {
        font-family: $font-headline;
        font-size: px2rem(32);
        line-height: px2rem(38);
        letter-spacing: px2rem(-1.5);
        font-style: italic;
        font-weight: 500;
        margin: 0 0 12px;
        padding: 0;

        @include bp(medium1) {
            margin: 0;
            padding: 0;
        }
    }

    p {
        @include body2-medium;

        margin: 0 auto;
        max-width: 300px;

        strong {
            font-size: px2rem(32);
            color: $parking-payment-hint-content-price;
            display: block;
            margin-block: px2rem(5);
        }
    }
}

.parking-payment-hint-content-illustration {
    width: 150px;
    margin: px2rem(20) auto;

    @include bp(medium1) {
        display: block;
        margin: px2rem(30) auto;
    }
}
</style>
